import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SeoMeta from "@/components/SeoMeta";
import { motion } from "framer-motion";
import { 
  Search, Filter, Star, Bot, Award, ChevronRight, Grid3X3, List, 
  MapPin, Navigation, Zap, ExternalLink, Users, Building2, User, Crown,
  Home, Car, Heart, Utensils, Briefcase, GraduationCap, Dumbbell, Scissors,
  PawPrint, Wrench, Sparkles, Paintbrush, Lightbulb, Shield, Waves, DoorOpen,
  Gauge, Bug, Laptop, Mail, BarChart3, Settings, CreditCard,
  Eye, X, Calendar, Tag, Sofa, Truck, ChevronLeft, ChevronsLeft, ChevronsRight, ArrowRight
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import BackgroundVideoStrip from "@/components/BackgroundVideoStrip";
import { BG_VIDEOS } from "@/data/backgroundVideos";
import axios from "axios";
import { VoiceSearchButton } from "@/components/VoiceSearchButton";
import ListingBenefitsSection from "@/components/ListingBenefitsSection";
import CheersLeaderboard from "@/components/CheersLeaderboard";
import BusinessOfTheMonthShowcase from "@/components/BusinessOfTheMonthShowcase";
import Homepage3DHero from "@/components/premium/Homepage3DHero";
import MarketMap3D from "@/components/premium/MarketMap3D";
import ListingCard3D from "@/components/premium/ListingCard3D";
import { flags } from "@/config/featureFlags";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// All US States
const US_STATES = [
  { code: "AL", name: "Alabama" }, { code: "AK", name: "Alaska" }, { code: "AZ", name: "Arizona" },
  { code: "AR", name: "Arkansas" }, { code: "CA", name: "California" }, { code: "CO", name: "Colorado" },
  { code: "CT", name: "Connecticut" }, { code: "DE", name: "Delaware" }, { code: "FL", name: "Florida" },
  { code: "GA", name: "Georgia" }, { code: "HI", name: "Hawaii" }, { code: "ID", name: "Idaho" },
  { code: "IL", name: "Illinois" }, { code: "IN", name: "Indiana" }, { code: "IA", name: "Iowa" },
  { code: "KS", name: "Kansas" }, { code: "KY", name: "Kentucky" }, { code: "LA", name: "Louisiana" },
  { code: "ME", name: "Maine" }, { code: "MD", name: "Maryland" }, { code: "MA", name: "Massachusetts" },
  { code: "MI", name: "Michigan" }, { code: "MN", name: "Minnesota" }, { code: "MS", name: "Mississippi" },
  { code: "MO", name: "Missouri" }, { code: "MT", name: "Montana" }, { code: "NE", name: "Nebraska" },
  { code: "NV", name: "Nevada" }, { code: "NH", name: "New Hampshire" }, { code: "NJ", name: "New Jersey" },
  { code: "NM", name: "New Mexico" }, { code: "NY", name: "New York" }, { code: "NC", name: "North Carolina" },
  { code: "ND", name: "North Dakota" }, { code: "OH", name: "Ohio" }, { code: "OK", name: "Oklahoma" },
  { code: "OR", name: "Oregon" }, { code: "PA", name: "Pennsylvania" }, { code: "RI", name: "Rhode Island" },
  { code: "SC", name: "South Carolina" }, { code: "SD", name: "South Dakota" }, { code: "TN", name: "Tennessee" },
  { code: "TX", name: "Texas" }, { code: "UT", name: "Utah" }, { code: "VT", name: "Vermont" },
  { code: "VA", name: "Virginia" }, { code: "WA", name: "Washington" }, { code: "WV", name: "West Virginia" },
  { code: "WI", name: "Wisconsin" }, { code: "WY", name: "Wyoming" }, { code: "DC", name: "Washington D.C." }
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.05 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

// TAP Dispatch Categories - One-tap access to local pros (image-driven)
const tapDispatchCategories = [
  { name: "Mortgage Lender", slug: "mortgage", image: "/assets/tap-dispatch/mortgage.webp", color: "#7C3AED" },
  { name: "Realtor", slug: "real-estate", image: "/assets/tap-dispatch/real-estate.webp", color: "#00E5FF" },
  { name: "Auto Dealers", slug: "auto-dealers", image: "/assets/tap-dispatch/auto-dealers.webp", color: "#EF4444", marketplace: "vehicles" },
  { name: "RV Dealers", slug: "rv-dealers", image: "/assets/tap-dispatch/rv-dealers.webp", color: "#06B6D4", marketplace: "rvs" },
  { name: "Furniture Stores", slug: "furniture", image: "/assets/tap-dispatch/furniture.webp", color: "#8B5CF6", marketplace: "furniture" },
  { name: "HVAC", slug: "hvac", image: "/assets/tap-dispatch/hvac.webp", color: "#F59E0B" },
  { name: "Roofing", slug: "roofing", image: "/assets/tap-dispatch/roofing.webp", color: "#EF4444" },
  { name: "Flooring", slug: "flooring", image: "/assets/tap-dispatch/flooring.webp", color: "#10B981" },
  { name: "Cleaning", slug: "cleaning", image: "/assets/tap-dispatch/cleaning.webp", color: "#3B82F6" },
  { name: "Painters", slug: "painters", image: "/assets/tap-dispatch/painters.webp", color: "#A855F7" },
  { name: "Construction", slug: "construction", image: "/assets/tap-dispatch/construction.webp", color: "#F97316" },
  { name: "Handyman", slug: "handyman", image: "/assets/tap-dispatch/handyman.webp", color: "#6366F1" },
  { name: "Plumbers", slug: "plumbers", image: "/assets/tap-dispatch/plumbers.webp", color: "#14B8A6" },
  { name: "Electricians", slug: "electrician", image: "/assets/tap-dispatch/electrician.webp", color: "#FACC15" },
  { name: "Landscaping", slug: "landscaping", image: "/assets/tap-dispatch/landscaping.webp", color: "#22C55E" },
  { name: "Windows & Doors", slug: "windows-doors", image: "/assets/tap-dispatch/windows-doors.webp", color: "#8B5CF6" },
  { name: "Smart Home", slug: "smart-home", image: "/assets/tap-dispatch/smart-home.webp", color: "#00E5FF" },
  { name: "Solar & Energy", slug: "solar-energy", image: "/assets/tap-dispatch/solar-energy.webp", color: "#F59E0B" },
  { name: "Pest Control", slug: "pest-control", image: "/assets/tap-dispatch/pest-control.webp", color: "#EF4444" },
  { name: "Garage Doors", slug: "garage-doors", image: "/assets/tap-dispatch/garage-doors.webp", color: "#94A3B8" },
  { name: "Pool & Spa", slug: "pool-spa", image: "/assets/tap-dispatch/pool-spa.webp", color: "#06B6D4" },
];

// Marketplace Search Links
const marketplaceSearchLinks = [
  { name: "Search Vehicles", slug: "vehicles", icon: Car, color: "#EF4444" },
  { name: "Search RVs", slug: "rvs", icon: Truck, color: "#06B6D4" },
  { name: "Search Homes", slug: "real_estate", icon: Home, color: "#EC4899" },
  { name: "Search Furniture", slug: "furniture", icon: Sofa, color: "#8B5CF6" },
  { name: "Search Jobs", slug: "jobs", icon: Briefcase, color: "#00E5FF" },
  { name: "Search Events", slug: "events", icon: Calendar, color: "#F59E0B" },
  { name: "Search Deals", slug: "deals", icon: Tag, color: "#10B981" },
];

// Special highlighted CTA button
const getLoanReadyButton = {
  name: "GET LOAN READY",
  icon: CreditCard,
  color: "#10B981",
  externalLink: "https://creditfirminc.net/credit-boost-624266?am_id=roehubbard3909",
  isHighlighted: true,
};

 

// Popular Categories - Categories with actual businesses
const popularCategories = [
  { name: "Professional Services", icon: Briefcase, slug: "professional-services" },
  { name: "Roofing", icon: Home, slug: "roofing" },
  { name: "HVAC", icon: Gauge, slug: "hvac" },
  { name: "Credit Services", icon: CreditCard, slug: "credit" },
  { name: "Legal", icon: Shield, slug: "legal" },
  { name: "Real Estate", icon: Building2, slug: "real-estate" },
  { name: "Landscaping", icon: PawPrint, slug: "landscaping" },
  { name: "Mortgage", icon: Building2, slug: "mortgage" },
  { name: "Plumbers", icon: Wrench, slug: "plumbers" },
  { name: "Local Services", icon: MapPin, slug: "local-services" },
];

// TAP Dispatch Card — image-driven category tile (refined editorial look)
function TapDispatchCard({ cat, idx, onClick }) {
  return (
    <motion.button
      variants={itemVariants}
      whileHover={{ y: -4 }}
      transition={{ duration: 0.2 }}
      onClick={onClick}
      className="group relative rounded-2xl overflow-hidden border border-white/10 bg-[#0A0F1E] hover:border-[#F59E0B]/50 hover:shadow-[0_8px_30px_rgba(245,158,11,0.18)] transition-all text-left"
      data-testid={`tap-${cat.slug}`}
      style={{ animationDelay: `${idx * 30}ms` }}
    >
      {/* Square image — no border-bottom seam; gradient covers label area */}
      <div className="aspect-square relative overflow-hidden bg-[#0A0F1E]">
        <img
          src={cat.image}
          alt={cat.name}
          loading="lazy"
          decoding="async"
          width="600"
          height="600"
          className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500 block"
          onError={(e) => { e.target.style.opacity = "0.3"; }}
        />
        {/* Bottom shadow ramp for label readability — overlaps label area */}
        <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#020817] via-[#020817]/70 to-transparent pointer-events-none" />
        {/* Color tint on hover */}
        <div
          className="absolute inset-0 opacity-0 group-hover:opacity-30 transition-opacity duration-300 mix-blend-overlay pointer-events-none"
          style={{ backgroundColor: cat.color }}
        />
        {/* Label sits inside image area, on top of gradient — no seam */}
        <div className="absolute inset-x-0 bottom-0 px-2.5 py-2.5 text-center">
          <p className="text-white text-xs sm:text-sm font-semibold leading-tight group-hover:text-[#F59E0B] transition-colors drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
            {cat.name}
          </p>
        </div>
      </div>
      {/* Accent left bar on hover */}
      <span
        className="absolute left-0 top-0 bottom-0 w-1 opacity-0 group-hover:opacity-100 transition-opacity"
        style={{ backgroundColor: cat.color }}
        aria-hidden="true"
      />
    </motion.button>
  );
}

// Card body — shared between Featured + Regular rows
function BusinessCardBody({ business, viewMode }) {
  return (
    <>
      <div className={viewMode === "list" ? "flex-1 p-4 flex items-center gap-4" : "p-4 sm:p-6"}>
        <div className={viewMode === "list" ? "flex-shrink-0" : "flex items-start justify-between mb-4"}>
          <img
            src={business.logo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(business.name)}&background=7C3AED&color=fff&size=128`}
            alt={`${business.name} - ${business.category_name} in ${business.city || "your area"}`}
            className={`rounded-xl object-cover bg-white/5 flex-shrink-0 ${viewMode === "list" ? "w-12 h-12" : "w-12 h-12 sm:w-14 sm:h-14"}`}
            onError={(e) => { e.target.onerror = null; e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(business.name)}&background=7C3AED&color=fff&size=128`; }}
          />
          {viewMode === "grid" && business.is_best_of_best && (
            <span className="best-badge text-xs"><Award className="w-3 h-3" /> Best</span>
          )}
        </div>
        <div className={viewMode === "list" ? "flex-1 min-w-0" : "min-w-0"}>
          <div className="flex items-center gap-2">
            <h3 className="text-white font-semibold font-['Outfit'] text-base sm:text-lg truncate">{business.name}</h3>
            {viewMode === "list" && business.is_best_of_best && (
              <span className="best-badge text-xs"><Award className="w-3 h-3" /></span>
            )}
            {business.is_individual && !business.listing_kind?.includes("creator") && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-[#00E5FF]/15 text-[#00E5FF] border border-[#00E5FF]/30 flex-shrink-0">Pro</span>
            )}
            {business.listing_kind === "creator" && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gradient-to-r from-[#FBBF24]/20 to-[#EC4899]/20 text-[#FBBF24] border border-[#FBBF24]/30 flex-shrink-0 flex items-center gap-0.5">
                <Crown className="w-2.5 h-2.5" /> Creator
              </span>
            )}
          </div>
          <p className="text-[#7C3AED] text-xs sm:text-sm mb-2">{business.category_name}</p>
          <p className="text-[#94A3B8] text-xs sm:text-sm line-clamp-2">{business.short_description}</p>
          <div className="flex items-center gap-2 sm:gap-3 mt-3 flex-wrap">
            <div className="flex items-center gap-1 text-[#F59E0B]">
              {[...Array(5)].map((_, i) => (<Star key={i} className="w-3 h-3 fill-current" />))}
              <Eye className="w-3 h-3 text-[#94A3B8] ml-1" />
            </div>
            {(business.city || business.state) && (
              <span className="text-[#94A3B8] text-xs flex items-center gap-1">
                <MapPin className="w-3 h-3 flex-shrink-0" />
                <span className="truncate max-w-[120px] sm:max-w-none">{[business.city, business.state].filter(Boolean).join(", ")}</span>
              </span>
            )}
          </div>
        </div>
        {viewMode === "list" && (<ChevronRight className="w-5 h-5 text-[#94A3B8] flex-shrink-0" />)}
      </div>
      {viewMode === "grid" && (
        <div className="px-6 py-4 bg-white/[0.02] border-t border-white/5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={business.ai_agent_avatar} alt={business.ai_agent_name} className="w-6 h-6 rounded-full" />
            <span className="text-[#94A3B8] text-sm">{business.ai_agent_name} • AI Hero™</span>
          </div>
          <Bot className="w-4 h-4 text-[#00E5FF]" />
        </div>
      )}
    </>
  );
}

export default function DirectoryPage() {
  const { category: categorySlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(categorySlug || "");
  const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
  const [viewMode, setViewMode] = useState("grid");
  const [isLoading, setIsLoading] = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [locationStatus, setLocationStatus] = useState("idle"); // idle, loading, success, error
  const [listingType, setListingType] = useState(searchParams.get("listing_type") || "all"); // all, businesses, individuals, best_of_best_business, best_of_best_pros
  const [sortBy, setSortBy] = useState(searchParams.get("sort") || "relevance"); // relevance, best_first, closest, highest_rated, newest
  
  // City and State filters
  const [cityFilter, setCityFilter] = useState(searchParams.get("city") || "");
  const [stateFilter, setStateFilter] = useState(searchParams.get("state") || "");
  const [showFilters, setShowFilters] = useState(false);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 24,
    total_count: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
  });
  const [seoUrls, setSeoUrls] = useState({ canonical: null, prev_url: null, next_url: null });
  
  // Above-the-fold compaction: collapses hero pill, description, and trust badges
  // as soon as the user starts typing in the search OR scrolls past the search bar.
  const [heroCollapsed, setHeroCollapsed] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      setHeroCollapsed(window.scrollY > 160);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const collapseHero = heroCollapsed || Boolean((searchQuery || "").trim());
  
  // Total count for display (from pagination)
  const totalBusinessCount = pagination.total_count;

  // Get current page from URL
  const currentPage = parseInt(searchParams.get("page") || "1", 10);

  // Get user location on mount
  useEffect(() => {
    if (!navigator.geolocation) {
      console.log("Geolocation not supported");
      setLocationStatus("error");
      return;
    }
    
    setLocationStatus("loading");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        });
        setLocationStatus("success");
      },
      (error) => {
        console.log("Geolocation error:", error.code, error.message);
        setLocationStatus("error");
      },
      { timeout: 15000, enableHighAccuracy: false, maximumAge: 60000 }
    );
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${API}/categories`);
        setCategories(res.data);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };
    fetchCategories();
  }, []);

  // Fetch businesses with server-side pagination
  useEffect(() => {
    const fetchBusinesses = async () => {
      setIsLoading(true);
      try {
        const params = new URLSearchParams();
        
        // Page number
        params.append("page", currentPage.toString());
        params.append("per_page", "24");
        
        // Category from URL path or selected
        const activeCategory = categorySlug || selectedCategory;
        if (activeCategory) {
          params.append("category", activeCategory);
          if (!categorySlug) setSelectedCategory(activeCategory);
        }
        
        if (searchQuery) {
          params.append("search", searchQuery);
        }

        if (listingType !== "all") {
          params.append("listing_type", listingType);
        }
        if (sortBy && sortBy !== "relevance") {
          params.append("sort", sortBy);
        }
        
        // City and state filters
        if (cityFilter) {
          params.append("city", cityFilter);
        }
        if (stateFilter) {
          params.append("state", stateFilter);
        }

        // Add location for geo-sorting
        if (userLocation) {
          params.append("lat", userLocation.lat);
          params.append("lng", userLocation.lng);
        }
        
        const res = await axios.get(`${API}/businesses/paginated?${params.toString()}`);
        setBusinesses(res.data.businesses);
        setPagination(res.data.pagination);
        setSeoUrls(res.data.seo);
      } catch (error) {
        console.error("Error fetching businesses:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    const debounce = setTimeout(fetchBusinesses, 300);
    return () => clearTimeout(debounce);
  }, [categorySlug, selectedCategory, searchQuery, currentPage, userLocation, listingType, cityFilter, stateFilter, sortBy]);

  // Update URL when filters change (reset to page 1)
  const updateFilters = useCallback((newFilters) => {
    const params = new URLSearchParams();
    
    // Always reset to page 1 when filters change
    const filters = { ...newFilters };
    
    if (filters.search) params.set("search", filters.search);
    if (filters.city) params.set("city", filters.city);
    if (filters.state) params.set("state", filters.state);
    if (filters.listing_type && filters.listing_type !== "all") params.set("listing_type", filters.listing_type);
    if (filters.sort && filters.sort !== "relevance") params.set("sort", filters.sort);
    // Don't set page=1 in URL (it's the default)
    
    setSearchParams(params);
  }, [setSearchParams]);

  // Navigate to a specific page
  const goToPage = useCallback((page) => {
    const params = new URLSearchParams(searchParams);
    if (page > 1) {
      params.set("page", page.toString());
    } else {
      params.delete("page");
    }
    setSearchParams(params);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [searchParams, setSearchParams]);

  const handleCategoryChange = (slug) => {
    const newCategory = slug === selectedCategory ? "" : slug;
    setSelectedCategory(newCategory);
    // Navigate to category URL or back to main directory
    if (newCategory) {
      navigate(`/directory/${newCategory}`);
    } else {
      navigate("/directory");
    }
  };
  
  const clearFilters = () => {
    setSelectedCategory("");
    setCityFilter("");
    setStateFilter("");
    setSearchQuery("");
    setListingType("all");
    setSearchParams(new URLSearchParams());
    if (categorySlug) {
      navigate("/directory");
    }
  };

  // Build canonical URL for current page
  const buildCanonicalUrl = () => {
    const baseUrl = window.location.origin;
    let path = categorySlug ? `/directory/${categorySlug}` : "/directory";
    const params = new URLSearchParams();
    if (currentPage > 1) params.set("page", currentPage.toString());
    if (searchQuery) params.set("search", searchQuery);
    if (cityFilter) params.set("city", cityFilter);
    if (stateFilter) params.set("state", stateFilter);
    const queryString = params.toString();
    return `${baseUrl}${path}${queryString ? `?${queryString}` : ""}`;
  };

  // Show full directory view when no category is selected
  const showFullDirectory = !categorySlug && !selectedCategory && !searchQuery && !cityFilter && !stateFilter;

  // Generate page title for SEO
  const getPageTitle = () => {
    const parts = ["LocalBest.ai"];
    if (categorySlug) {
      const cat = categories.find((c) => c.slug === categorySlug);
      if (cat) parts.unshift(cat.name);
    }
    if (cityFilter || stateFilter) {
      const location = [cityFilter, stateFilter].filter(Boolean).join(", ");
      if (location) parts.unshift(location);
    }
    if (currentPage > 1) parts.push(`Page ${currentPage}`);
    return parts.join(" | ");
  };

  // Pagination component for SEO-friendly crawlable links
  const PaginationControls = () => {
    if (pagination.total_pages <= 1) return null;
    
    const { current_page, total_pages, has_prev, has_next } = pagination;
    
    // Generate page numbers to show
    const getPageNumbers = () => {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, current_page - Math.floor(maxVisible / 2));
      let end = Math.min(total_pages, start + maxVisible - 1);
      
      if (end - start < maxVisible - 1) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    };
    
    // Build href for crawlable links
    const buildPageHref = (page) => {
      const params = new URLSearchParams();
      if (page > 1) params.set("page", page.toString());
      if (searchQuery) params.set("search", searchQuery);
      if (cityFilter) params.set("city", cityFilter);
      if (stateFilter) params.set("state", stateFilter);
      if (listingType !== "all") params.set("listing_type", listingType);
      const queryString = params.toString();
      const basePath = categorySlug ? `/directory/${categorySlug}` : "/directory";
      return `${basePath}${queryString ? `?${queryString}` : ""}`;
    };
    
    return (
      <nav className="flex flex-col items-center gap-4 mt-8" aria-label="Pagination" data-testid="pagination-nav">
        <div className="flex items-center gap-2">
          {/* First page */}
          {current_page > 2 && (
            <Link
              to={buildPageHref(1)}
              onClick={(e) => { e.preventDefault(); goToPage(1); }}
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-[#94A3B8] hover:text-white transition-colors"
              aria-label="First page"
              data-testid="pagination-first"
            >
              <ChevronsLeft className="w-5 h-5" />
            </Link>
          )}
          
          {/* Previous page */}
          {has_prev && (
            <Link
              to={buildPageHref(current_page - 1)}
              onClick={(e) => { e.preventDefault(); goToPage(current_page - 1); }}
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-[#94A3B8] hover:text-white transition-colors"
              aria-label="Previous page"
              rel="prev"
              data-testid="pagination-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </Link>
          )}
          
          {/* Page numbers */}
          <div className="flex items-center gap-1">
            {getPageNumbers().map((pageNum) => (
              <Link
                key={pageNum}
                to={buildPageHref(pageNum)}
                onClick={(e) => { e.preventDefault(); goToPage(pageNum); }}
                className={`min-w-[40px] h-10 flex items-center justify-center rounded-lg text-sm font-medium transition-colors ${
                  pageNum === current_page
                    ? "bg-[#7C3AED] text-white"
                    : "bg-white/5 text-[#94A3B8] hover:bg-white/10 hover:text-white"
                }`}
                aria-current={pageNum === current_page ? "page" : undefined}
                data-testid={`pagination-page-${pageNum}`}
              >
                {pageNum}
              </Link>
            ))}
          </div>
          
          {/* Next page */}
          {has_next && (
            <Link
              to={buildPageHref(current_page + 1)}
              onClick={(e) => { e.preventDefault(); goToPage(current_page + 1); }}
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-[#94A3B8] hover:text-white transition-colors"
              aria-label="Next page"
              rel="next"
              data-testid="pagination-next"
            >
              <ChevronRight className="w-5 h-5" />
            </Link>
          )}
          
          {/* Last page */}
          {current_page < total_pages - 1 && (
            <Link
              to={buildPageHref(total_pages)}
              onClick={(e) => { e.preventDefault(); goToPage(total_pages); }}
              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-[#94A3B8] hover:text-white transition-colors"
              aria-label="Last page"
              data-testid="pagination-last"
            >
              <ChevronsRight className="w-5 h-5" />
            </Link>
          )}
        </div>
        
        {/* Page info */}
        <p className="text-[#64748B] text-sm">
          Page {current_page} of {total_pages} • {pagination.total_count.toLocaleString()} total listings
        </p>
      </nav>
    );
  };

  // Update document head with SEO link tags
  useEffect(() => {
    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = buildCanonicalUrl();
    
    // Prev link
    let prevLink = document.querySelector('link[rel="prev"]');
    if (seoUrls.prev_url) {
      if (!prevLink) {
        prevLink = document.createElement('link');
        prevLink.rel = 'prev';
        document.head.appendChild(prevLink);
      }
      prevLink.href = `${window.location.origin}${seoUrls.prev_url}`;
    } else if (prevLink) {
      prevLink.remove();
    }
    
    // Next link
    let nextLink = document.querySelector('link[rel="next"]');
    if (seoUrls.next_url) {
      if (!nextLink) {
        nextLink = document.createElement('link');
        nextLink.rel = 'next';
        document.head.appendChild(nextLink);
      }
      nextLink.href = `${window.location.origin}${seoUrls.next_url}`;
    } else if (nextLink) {
      nextLink.remove();
    }
    
    // Robots meta for pages > 1
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (currentPage > 1) {
      if (!robotsMeta) {
        robotsMeta = document.createElement('meta');
        robotsMeta.name = 'robots';
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.content = 'noindex, follow';
    } else if (robotsMeta) {
      robotsMeta.remove();
    }
    
    return () => {
      // Cleanup on unmount
      document.querySelector('link[rel="prev"]')?.remove();
      document.querySelector('link[rel="next"]')?.remove();
      document.querySelector('meta[name="robots"]')?.remove();
    };
  }, [currentPage, seoUrls, buildCanonicalUrl]);

  return (
    <div className="page-container min-h-screen" data-testid="directory-page">
      {/* SEO Meta Tags — homepage / category fallback. Listing pages override via SEOHead. */}
      <SeoMeta
        title={getPageTitle()}
        description={`Browse ${pagination.total_count.toLocaleString()} AI-powered business listings${categorySlug ? ` in ${categorySlug}` : ""}. Find trusted local professionals with AI Business Heroes.`}
        ogType="website"
        ogSiteName="LocalBest.ai — The AI Horse-Powered Best of Best Directory™"
        ogTitle={getPageTitle()}
        ogDescription="LocalBest.ai — The AI Horse-Powered Best of Best Directory™. List once. Get seen everywhere."
        ogImage="https://localbest.ai/assets/localbest-social-share.png"
        twitterCard="summary_large_image"
        twitterTitle={getPageTitle()}
        twitterDescription="LocalBest.ai — The AI Horse-Powered Best of Best Directory™."
        twitterImage="https://localbest.ai/assets/localbest-social-share.png"
      />

      {/* Premium 3D Homepage Hero — shown only on the homepage (no category filter), feature-flagged. */}
      {!categorySlug && !searchQuery && flags.ENABLE_3D_HOMEPAGE_HERO && (
        <Homepage3DHero
          featuredListings={businesses.filter((b) => b.is_best_of_best).slice(0, 3)}
          stats={{
            total_businesses: pagination.total_count,
            best_of_best: businesses.filter((b) => b.is_best_of_best).length,
            categories: categories.length,
          }}
        />
      )}

      {/* Hero Banner */}
      <section className={`hero-pattern relative overflow-hidden transition-all ${collapseHero ? "py-4 md:py-6 pb-6" : "py-10 md:py-16 pb-24 md:pb-16 lg:pb-16"}`}>
        <div className="hero-glow top-0 right-1/4 opacity-40" />
        <div className="hero-glow top-0 left-1/4 opacity-30" style={{ background: "radial-gradient(circle, rgba(0,229,255,0.3) 0%, transparent 70%)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            {categorySlug ? (
              <>
                <h1 className="text-3xl md:text-5xl font-bold font-['Outfit'] text-white mb-4">
                  {categories.find((c) => c.slug === categorySlug)?.name || "Directory"}
                </h1>
                <p className="text-[#94A3B8] text-lg max-w-2xl mx-auto mb-6">
                  Discover AI-certified businesses and professionals in your area
                </p>
              </>
            ) : (
              <>
                {!collapseHero && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.1 }}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#7C3AED]/20 to-[#00E5FF]/20 border border-[#7C3AED]/30 mb-4"
                >
                  <Zap className="w-4 h-4 text-[#00E5FF]" />
                  <span className="text-sm text-[#00E5FF] font-medium">LocalBest.ai</span>
                </motion.div>
                )}
                
                <h1 className={`font-bold font-['Outfit'] text-white leading-tight max-w-5xl mx-auto transition-all ${collapseHero ? "text-2xl sm:text-3xl mb-3" : "text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-4"}`}>
                  Everything You're Looking For —{" "}
                  <span className="bg-gradient-to-r from-[#7C3AED] via-[#00E5FF] to-[#10B981] bg-clip-text text-transparent">
                    Found Faster
                  </span>{" "}
                  with AI Horse-Power
                </h1>
                
                {!collapseHero && (
                <p className="text-[#94A3B8] text-base sm:text-lg max-w-2xl mx-auto mb-4">
                  Search the most advanced AI-powered directory ever created.
                </p>
                )}
                
                {/* Directory Search Box — AI Horse-Powered */}
                <div className="max-w-2xl mx-auto mb-6">
                  <div className="flex items-stretch gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                      <input
                        type="text"
                        placeholder="Ask LocalBest AI... 'Find me a roofer in Chicago'"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && searchQuery.trim()) {
                            navigate(`/ai-search?q=${encodeURIComponent(searchQuery.trim())}`);
                          }
                        }}
                        className="w-full pl-12 pr-14 py-4 rounded-xl bg-white/10 border border-white/20 text-white placeholder:text-[#64748B] focus:border-[#7C3AED] focus:ring-2 focus:ring-[#7C3AED]/20 focus:outline-none transition-all text-base"
                        data-testid="directory-search-input"
                      />
                      {/* Voice Search Button */}
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <VoiceSearchButton 
                          onResult={(text) => setSearchQuery(text)} 
                          size="default"
                        />
                      </div>
                    </div>
                    <button
                      onClick={() => searchQuery.trim() && navigate(`/ai-search?q=${encodeURIComponent(searchQuery.trim())}`)}
                      className="px-5 rounded-xl bg-gradient-to-r from-[#7C3AED] to-[#6D28D9] hover:from-[#8B5CF6] hover:to-[#7C3AED] text-white font-medium text-sm sm:text-base flex items-center gap-2 whitespace-nowrap transition-all shadow-[0_0_20px_rgba(124,58,237,0.3)]"
                      data-testid="directory-ask-ai-btn"
                    >
                      <Sparkles className="w-4 h-4" />
                      Ask AI
                    </button>
                  </div>
                  <p className="text-[#64748B] text-xs mt-2">Press Enter or tap Ask AI for a smart answer — or keep typing to filter listings below.</p>
                </div>
                
                {!collapseHero && (
                <p className="text-[#64748B] text-xs sm:text-base max-w-3xl mx-auto leading-relaxed text-center">
                  Explore <span className="text-[#7C3AED]">Best of Best Businesses</span>, top professionals, and curated marketplace listings for{" "}
                  <span className="text-[#EF4444]">vehicles</span>, <span className="text-[#EC4899]">homes</span>,{" "}
                  <span className="text-[#8B5CF6]">furniture</span>, <span className="text-[#06B6D4]">RVs</span>,{" "}
                  <span className="text-[#10B981]">deals</span>, <span className="text-[#00E5FF]">jobs</span>, and{" "}
                  <span className="text-[#F59E0B]">events</span> — all supported by{" "}
                  <span className="text-[#00E5FF] font-semibold">AI Business Hero Agents</span> that answer questions, unlock incentives, and guide you<span className="hidden sm:inline"> instantly</span>.
                </p>
                )}
              </>
            )}
            
            {/* Location Status */}
            {locationStatus === "success" && (
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#10B981]/10 border border-[#10B981]/20 mt-4">
                <MapPin className="w-4 h-4 text-[#10B981]" />
                <span className="text-sm text-[#10B981]">Showing results near you</span>
              </div>
            )}
            {locationStatus === "loading" && (
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/20 mt-4">
                <Navigation className="w-4 h-4 text-[#F59E0B] animate-pulse" />
                <span className="text-sm text-[#F59E0B]">Getting your location...</span>
              </div>
            )}
            
            {/* Trust Badges */}
            {!collapseHero && (
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 mt-8">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/30">
                <Shield className="w-4 h-4 text-[#F59E0B]" />
                <span className="text-xs sm:text-sm text-[#F59E0B] font-medium">Cloudflare Protected</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#10B981]/10 border border-[#10B981]/30">
                <Shield className="w-4 h-4 text-[#10B981]" />
                <span className="text-xs sm:text-sm text-[#10B981] font-medium">Secure Platform</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#7C3AED]/10 border border-[#7C3AED]/30">
                <Sparkles className="w-4 h-4 text-[#7C3AED]" />
                <span className="text-xs sm:text-sm text-[#7C3AED] font-medium">LocalBest.ai™</span>
              </div>
            </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Marketplace Search Section */}
      {showFullDirectory && (
        <section className="pt-16 pb-8 lg:pt-8 bg-gradient-to-b from-transparent to-[#020817]/50 relative overflow-visible">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            {/* AI Horse Character GIF - Desktop: higher and to the right, chest level with search box */}
            <div className="absolute right-[-140px] top-[-420px] w-[450px] h-[600px] pointer-events-none z-0 hidden lg:block">
              <img 
                src="https://customer-assets.emergentagent.com/job_b1b5869b-65cc-4b1f-ac0a-6c8ed691ccb8/artifacts/d3f5wiek_Untitled%20design%20%2832%29.gif"
                alt="AI Horse Hero"
                className="w-full h-full object-contain opacity-85"
              />
            </div>
            
            {/* AI Horse Character GIF - Mobile: centered-left, next to Search RVs card */}
            <div className="absolute left-[35%] top-[-140px] w-[160px] h-[220px] pointer-events-none block lg:hidden" style={{ zIndex: 0 }}>
              <img 
                src="https://customer-assets.emergentagent.com/job_b1b5869b-65cc-4b1f-ac0a-6c8ed691ccb8/artifacts/d3f5wiek_Untitled%20design%20%2832%29.gif"
                alt="AI Horse Hero"
                className="w-full h-full object-contain opacity-70"
              />
            </div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 relative z-10"
            >
              {marketplaceSearchLinks.map((item, idx) => (
                <motion.a
                  key={idx}
                  variants={itemVariants}
                  href={`/marketplace/${item.slug}`}
                  className="glass-card p-4 rounded-xl text-center hover:border-[#00E5FF]/50 transition-all group relative z-10"
                  data-testid={`marketplace-${item.slug}`}
                >
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-2 group-hover:scale-110 transition-transform"
                    style={{ backgroundColor: `${item.color}20` }}
                  >
                    <item.icon className="w-6 h-6" style={{ color: item.color }} />
                  </div>
                  <span className="text-white text-sm font-medium leading-tight block">{item.name}</span>
                </motion.a>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* TAP Dispatch Section */}
      {showFullDirectory && (
        <section className="py-14 bg-gradient-to-b from-[#020817] via-[#0A0F1E] to-transparent relative overflow-hidden">
          {/* Soft glow accents */}
          <div className="pointer-events-none absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#F59E0B]/10 blur-[120px]" />
          <div className="pointer-events-none absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-[#7C3AED]/10 blur-[120px]" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3"
            >
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gradient-to-r from-[#F59E0B]/15 to-[#EF4444]/15 border border-[#F59E0B]/30 mb-3">
                  <Zap className="w-3.5 h-3.5 text-[#F59E0B]" />
                  <span className="text-[11px] uppercase tracking-[0.2em] text-[#F59E0B] font-bold">One-Tap Access</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-white">
                  TAP <span className="bg-gradient-to-r from-[#F59E0B] via-[#EF4444] to-[#7C3AED] bg-clip-text text-transparent">Dispatch</span>
                </h2>
                <p className="text-[#94A3B8] mt-1 max-w-xl">TAP a Button & Save Time & Money — Find the Best of Best in your local area instantly.</p>
              </div>
            </motion.div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4"
            >
              {tapDispatchCategories.map((cat, idx) => (
                <TapDispatchCard
                  key={cat.slug}
                  cat={cat}
                  idx={idx}
                  onClick={() => {
                    if (cat.marketplace) {
                      navigate(`/marketplace/${cat.marketplace}`);
                    } else {
                      handleCategoryChange(cat.slug);
                    }
                  }}
                />
              ))}

              {/* GET LOAN READY — featured horizontal banner spanning the trailing empty cells */}
              <motion.a
                variants={itemVariants}
                href={getLoanReadyButton.externalLink}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ y: -4 }}
                className="group relative rounded-2xl overflow-hidden border-2 border-[#10B981] shadow-[0_0_30px_rgba(16,185,129,0.35)] hover:shadow-[0_0_45px_rgba(16,185,129,0.6)] bg-gradient-to-br from-[#064E3B] to-[#020817] transition-shadow col-span-2 sm:col-span-3 md:col-span-3 lg:col-span-4 flex items-stretch"
                data-testid="tap-get-loan-ready"
              >
                {/* Shine sweep */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000 z-10 pointer-events-none" />

                {/* Image — left portion only */}
                <div className="relative w-2/5 sm:w-1/3 md:w-1/3 lg:w-1/4 flex-shrink-0 overflow-hidden">
                  <img
                    src="/assets/tap-dispatch/get-loan-ready.webp"
                    alt="Get Loan Ready"
                    loading="lazy"
                    decoding="async"
                    width="600"
                    height="600"
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#064E3B]/80" />
                </div>

                {/* Right text content */}
                <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 md:px-8 py-4 text-left relative">
                  <span className="inline-flex w-fit items-center gap-1 px-2 py-0.5 rounded-full bg-[#10B981] text-[10px] text-white font-bold uppercase tracking-wider shadow-lg mb-2">
                    Featured
                  </span>
                  <p className="text-white text-base sm:text-xl md:text-2xl font-bold leading-tight font-['Outfit']">
                    {getLoanReadyButton.name}
                  </p>
                  <p className="text-[#A7F3D0] text-xs sm:text-sm mt-1.5 leading-snug hidden sm:block">
                    Boost your credit score before applying for funding.
                  </p>
                  <span className="inline-flex items-center gap-1.5 text-[#10B981] text-xs font-bold mt-2 group-hover:gap-2.5 transition-all">
                    Start now <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </motion.a>
            </motion.div>
          </div>
        </section>
      )}


      {/* OnlyBest Creator Movement banner (homepage only) */}
      {showFullDirectory && (
        <section className="py-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Link to="/onlybest" data-testid="onlybest-home-cta" className="block group">
              <div className="relative overflow-hidden rounded-2xl border border-[#DC2626]/40 bg-[#0A1020] shadow-[0_0_60px_-25px_rgba(220,38,38,0.35)] hover:border-[#2563EB]/50 transition-all">
                {/* Background image (patriotic) + dark overlay */}
                <div className="absolute inset-0">
                  <img
                    src="/assets/onlybest_hero.png"
                    alt=""
                    aria-hidden="true"
                    className="absolute inset-0 w-full h-full object-cover object-center opacity-40 group-hover:opacity-55 transition-opacity duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-[#0A1020] via-[#0A1020]/85 to-[#0A1020]/30" />
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_50%,rgba(220,38,38,0.25),transparent_50%),radial-gradient(circle_at_85%_50%,rgba(37,99,235,0.25),transparent_50%)]" />
                </div>

                {/* Content */}
                <div className="relative flex flex-col sm:flex-row sm:items-center gap-4 px-5 py-6 sm:px-7 sm:py-7">
                  <span className="inline-flex w-12 h-12 rounded-xl bg-gradient-to-br from-[#DC2626] to-[#2563EB] items-center justify-center text-white flex-shrink-0 shadow-[0_0_25px_rgba(220,38,38,0.5)]">
                    <Crown className="w-6 h-6" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] sm:text-xs uppercase tracking-[0.2em] sm:tracking-[0.25em] text-[#DC2626] mb-1 font-bold">
                      OnlyBest™ — Join the Revolution
                    </p>
                    <p className="text-white font-bold text-base sm:text-lg lg:text-xl leading-tight font-['Outfit']">
                      Want to make money{" "}
                      <span className="bg-gradient-to-r from-[#DC2626] via-white to-[#2563EB] bg-clip-text text-transparent">
                        recommending the best?
                      </span>
                    </p>
                    <p className="text-[#C7D2E0] text-xs sm:text-sm mt-1.5">
                      Become an OnlyBest Creator and earn by promoting businesses, causes, deals, events, and community favorites.
                    </p>
                  </div>
                  <span className="inline-flex items-center justify-center gap-1.5 w-full sm:w-auto px-5 py-3 rounded-full bg-gradient-to-r from-[#DC2626] to-[#2563EB] text-white text-sm font-bold group-hover:scale-105 group-hover:shadow-[0_0_30px_rgba(37,99,235,0.6)] transition-all flex-shrink-0">
                    Create My OnlyBest Account
                  </span>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Cheers Leaderboard — homepage social proof / viral loop */}
      {showFullDirectory && (
        <section className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <CheersLeaderboard limit={8} />
          </div>
        </section>
      )}

      {/* Best of Best — Business of the Month showcase (replaces the prior marquee) */}
      {showFullDirectory && <BusinessOfTheMonthShowcase />}

      {/* Popular Categories Section */}
      {showFullDirectory && (
        <section className="py-12 bg-white/[0.01]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-8"
            >
              <h2 className="text-2xl font-bold font-['Outfit'] text-white mb-2">Popular Categories</h2>
              <p className="text-[#94A3B8]">Browse businesses by what they do best</p>
            </motion.div>

            <motion.div
              variants={containerVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4"
            >
              {popularCategories.map((cat, idx) => (
                <motion.div key={idx} variants={itemVariants}>
                  <Link
                    to={`/directory/${cat.slug}`}
                    className="glass-card p-4 rounded-xl flex items-center gap-3 hover:border-[#00E5FF]/50 transition-all group"
                  >
                    <div className="w-10 h-10 rounded-lg bg-[#00E5FF]/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <cat.icon className="w-5 h-5 text-[#00E5FF]" />
                    </div>
                    <span className="text-white font-medium text-sm">{cat.name}</span>
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* Main Directory Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="sticky top-24 space-y-6">
              {/* Near You Location Filter */}
              <div className="glass-card p-4 rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                  <MapPin className="w-4 h-4 text-[#10B981]" />
                  <h3 className="text-white font-semibold">Near You</h3>
                </div>
                <div className="space-y-3">
                  {locationStatus === "success" && userLocation && (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#10B981]/20 text-[#10B981] text-sm">
                      <Navigation className="w-4 h-4" />
                      <span>Location enabled</span>
                    </div>
                  )}
                  {locationStatus === "loading" && (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#F59E0B]/20 text-[#F59E0B] text-sm">
                      <Navigation className="w-4 h-4 animate-pulse" />
                      <span>Getting location...</span>
                    </div>
                  )}
                  {locationStatus === "error" && (
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[#EF4444]/20 text-[#EF4444] text-sm">
                        <MapPin className="w-4 h-4" />
                        <span>Location unavailable</span>
                      </div>
                      <Button 
                        size="sm"
                        variant="outline"
                        className="btn-secondary w-full text-xs"
                        data-testid="retry-location-btn"
                        onClick={() => {
                          if (!navigator.geolocation) {
                            alert("Location is not supported on this browser. Please try using a different browser or enable location services in your device settings.");
                            return;
                          }
                          setLocationStatus("loading");
                          navigator.geolocation.getCurrentPosition(
                            (position) => {
                              setUserLocation({
                                lat: position.coords.latitude,
                                lng: position.coords.longitude,
                              });
                              setLocationStatus("success");
                            },
                            (error) => {
                              console.log("Geolocation retry error:", error.code, error.message);
                              setLocationStatus("error");
                              if (error.code === 1) {
                                alert("Location permission denied. Please allow location access in your browser settings and try again.");
                              } else if (error.code === 2) {
                                alert("Location unavailable. Please check your device's location settings or try again later.");
                              } else if (error.code === 3) {
                                alert("Location request timed out. Please check your connection and try again.");
                              }
                            },
                            { timeout: 15000, enableHighAccuracy: false, maximumAge: 60000 }
                          );
                        }}
                      >
                        Try Again
                      </Button>
                      <p className="text-[#64748B] text-xs mt-1">
                        Tip: Check your browser and device location settings
                      </p>
                    </div>
                  )}
                  {locationStatus === "idle" && (
                    <Button 
                      size="sm"
                      className="w-full bg-[#10B981] hover:bg-[#10B981]/80"
                      data-testid="enable-location-btn"
                      onClick={() => {
                        if (!navigator.geolocation) {
                          alert("Location is not supported on this browser.");
                          setLocationStatus("error");
                          return;
                        }
                        setLocationStatus("loading");
                        navigator.geolocation.getCurrentPosition(
                          (position) => {
                            setUserLocation({
                              lat: position.coords.latitude,
                              lng: position.coords.longitude,
                            });
                            setLocationStatus("success");
                          },
                          (error) => {
                            console.log("Geolocation error:", error.code, error.message);
                            setLocationStatus("error");
                          },
                          { timeout: 15000, enableHighAccuracy: false, maximumAge: 60000 }
                        );
                      }}
                    >
                      <MapPin className="w-4 h-4 mr-2" />
                      Enable Location
                    </Button>
                  )}
                  <p className="text-[#64748B] text-xs">
                    {locationStatus === "success" 
                      ? "Results sorted by distance from your location"
                      : "Enable to see businesses near you first"
                    }
                  </p>
                </div>
              </div>

              {/* Listing Type Filter */}
              <div className="glass-card p-4 rounded-xl">
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-4 h-4 text-[#00E5FF]" />
                  <h3 className="text-white font-semibold">Listing Type</h3>
                </div>
                <div className="space-y-2">
                  {[
                    { key: "all", icon: Grid3X3, label: "All Listings", testid: "filter-listing-type-all" },
                    { key: "best_of_best_business", icon: Award, label: "Best of Best Business", badge: true, testid: "filter-listing-type-bob-biz" },
                    { key: "best_of_best_pros", icon: Award, label: "Best of Best Pros", badge: true, testid: "filter-listing-type-bob-pros" },
                    { key: "businesses", icon: Building2, label: "Businesses", testid: "filter-listing-type-businesses" },
                    { key: "individuals", icon: User, label: "Pros / Agents", testid: "filter-listing-type-pros" },
                    { key: "creators", icon: Crown, label: "OnlyBest Creators", testid: "filter-listing-type-creators" },
                  ].map(({ key, icon: Icon, label, badge, testid }) => (
                    <button
                      key={key}
                      onClick={() => setListingType(key)}
                      data-testid={testid}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                        listingType === key
                          ? (badge ? "bg-[#F59E0B]/15 text-[#FBBF24] border border-[#F59E0B]/30" : "bg-[#00E5FF]/20 text-[#00E5FF]")
                          : "text-[#94A3B8] hover:text-white hover:bg-white/5"
                      }`}
                    >
                      <Icon className={`w-4 h-4 ${badge && listingType === key ? "text-[#FBBF24]" : ""}`} />
                      <span className="flex-1">{label}</span>
                      {badge && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${listingType === key ? "bg-[#F59E0B]/30 text-[#FBBF24]" : "bg-white/5 text-[#F59E0B]"}`}>★</span>}
                    </button>
                  ))}
                </div>
              </div>

              {/* Categories Filter */}
              <div className="glass-card p-4 rounded-xl">
                <div className="flex items-center gap-2 mb-4">
                  <Filter className="w-4 h-4 text-[#7C3AED]" />
                  <h3 className="text-white font-semibold">Categories</h3>
                </div>
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  <button
                    onClick={() => handleCategoryChange("")}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      !selectedCategory
                        ? "bg-[#7C3AED]/20 text-[#7C3AED]"
                        : "text-[#94A3B8] hover:text-white hover:bg-white/5"
                    }`}
                    data-testid="filter-all"
                  >
                    All Categories
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      onClick={() => handleCategoryChange(cat.slug)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                        selectedCategory === cat.slug
                          ? "bg-[#7C3AED]/20 text-[#7C3AED]"
                          : "text-[#94A3B8] hover:text-white hover:bg-white/5"
                      }`}
                      data-testid={`filter-${cat.slug}`}
                    >
                      <span>{cat.icon}</span>
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            {/* Results Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
              <div className="min-w-0">
                <p className="text-[#94A3B8] text-sm sm:text-base">
                  <span className="text-white font-medium">{pagination.total_count.toLocaleString()}</span> AI Business Hero Listings
                  {pagination.total_pages > 1 && (
                    <span className="text-[#64748B]"> • Page {pagination.current_page} of {pagination.total_pages}</span>
                  )}
                  {userLocation && <span className="text-[#10B981] ml-2">• Near you</span>}
                </p>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
                <div className="relative flex-1 sm:flex-initial">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="appearance-none w-full bg-white/5 border border-white/10 hover:border-white/20 text-white text-sm rounded-lg pl-3 pr-9 py-2 focus:outline-none focus:border-[#00E5FF] cursor-pointer"
                    data-testid="sort-by-select"
                    aria-label="Sort results"
                  >
                    <option value="relevance" className="bg-[#0B1226]">Sort: Relevance</option>
                    <option value="best_first" className="bg-[#0B1226]">Best of Best first</option>
                    <option value="closest" className="bg-[#0B1226]" disabled={!userLocation}>Closest to me{userLocation ? "" : " (enable location)"}</option>
                    <option value="highest_rated" className="bg-[#0B1226]">Highest rated</option>
                    <option value="newest" className="bg-[#0B1226]">Newest</option>
                  </select>
                  <ChevronRight className="w-4 h-4 text-[#94A3B8] absolute right-2.5 top-1/2 -translate-y-1/2 rotate-90 pointer-events-none" />
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setViewMode("grid")}
                  className={`flex-shrink-0 ${viewMode === "grid" ? "text-white" : "text-[#94A3B8]"}`}
                  aria-label="Grid view"
                >
                  <Grid3X3 className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setViewMode("list")}
                  className={`flex-shrink-0 ${viewMode === "list" ? "text-white" : "text-[#94A3B8]"}`}
                  aria-label="List view"
                >
                  <List className="w-5 h-5" />
                </Button>
              </div>
            </div>

            {/* Business Grid/List */}
            {isLoading ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="glass-card rounded-2xl p-6 animate-pulse">
                    <div className="w-14 h-14 rounded-xl bg-white/10 mb-4" />
                    <div className="h-5 bg-white/10 rounded w-3/4 mb-2" />
                    <div className="h-4 bg-white/10 rounded w-1/2 mb-4" />
                    <div className="h-16 bg-white/10 rounded" />
                  </div>
                ))}
              </div>
            ) : businesses.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-[#94A3B8]" />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">No listings found</h3>
                <p className="text-[#94A3B8]">Try adjusting your search or filters</p>
              </div>
            ) : (
              <>
              {/* Fallback banner — searched location returned no exact-city matches */}
              {cityFilter && businesses.length > 0 &&
                !businesses.some(b => b.match_tier === "exact_city") && (
                <div className="mb-5 px-4 py-3 rounded-lg bg-[#F59E0B]/10 border border-[#F59E0B]/30 text-sm text-[#FBBF24] flex items-start gap-2" data-testid="nearby-results-banner">
                  <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    No exact matches in <strong>{cityFilter}</strong>{stateFilter ? `, ${stateFilter.toUpperCase()}` : ""}. Showing the closest nearby Best-of-Best listings instead.
                  </span>
                </div>
              )}
              {/* Split: when "All Listings" + page 1, show Featured (Best of Best) as its own row first */}
              {(() => {
                const showFeaturedRow = listingType === "all" && currentPage === 1;
                const featuredList = showFeaturedRow ? businesses.filter((b) => b.is_best_of_best) : [];
                const regularList = showFeaturedRow ? businesses.filter((b) => !b.is_best_of_best) : businesses;

                const renderCard = (business) => {
                  const citySlug = (business.city || "local").toLowerCase().replace(/\s+/g, "-");
                  const stateSlug = (business.state || "").toLowerCase();
                  const cityState = stateSlug ? `${citySlug}-${stateSlug}` : citySlug;
                  const seoUrl = `/directory/${business.category_id || "business"}/${cityState}/${business.slug}`;
                  return (
                    <motion.div key={business.id} variants={itemVariants}>
                      <ListingCard3D business={business}>
                        <Link
                          to={seoUrl}
                          onClick={() => window.scrollTo(0, 0)}
                          className={`block glass-card rounded-2xl overflow-hidden card-lift ${viewMode === "list" ? "flex items-center" : ""}`}
                          data-testid={`business-card-${business.slug}`}
                        >
                          <BusinessCardBody business={business} viewMode={viewMode} />
                        </Link>
                      </ListingCard3D>
                    </motion.div>
                  );
                };

                return (
                  <>
                    {featuredList.length > 0 && (
                      <div className="mb-6" data-testid="featured-row">
                        <div className="flex items-center gap-2 mb-3">
                          <Award className="w-5 h-5 text-[#F59E0B]" />
                          <h2 className="text-white font-bold text-lg">Featured · Best of Best</h2>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-[#F59E0B]/15 text-[#FBBF24] border border-[#F59E0B]/30">{featuredList.length}</span>
                        </div>
                        <motion.div
                          variants={containerVariants}
                          initial="hidden"
                          animate="visible"
                          className={viewMode === "grid" ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" : "space-y-4"}
                        >
                          {featuredList.map(renderCard)}
                        </motion.div>
                        {regularList.length > 0 && (
                          <div className="flex items-center gap-3 my-6">
                            <div className="flex-1 h-px bg-white/5" />
                            <span className="text-xs uppercase tracking-wider text-[#64748B]">All Listings</span>
                            <div className="flex-1 h-px bg-white/5" />
                          </div>
                        )}
                      </div>
                    )}
                    <motion.div
                      variants={containerVariants}
                      initial="hidden"
                      animate="visible"
                      className={viewMode === "grid" ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" : "space-y-4"}
                    >
                      {regularList.map(renderCard)}
                    </motion.div>
                  </>
                );
              })()}
              
              {/* Pagination Controls */}
              <PaginationControls />
              </>
            )}
          </main>
        </div>
      </div>

      {/* MarketMap3D — Best of Best Territory Map (homepage only) */}
      {showFullDirectory && flags.ENABLE_3D_MARKET_MAP && (
        <section className="py-12 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="directory-market-map-section">
          <MarketMap3D
            categories={categories.slice(0, 6)}
            compact
            title="Best of Best Territory Map"
            subtitle="Live look at which markets are claimed, sponsored, or still open. Reserve your category before someone else does."
          />
        </section>
      )}

      {/* === ACTIVE BEST OF BEST LISTING BENEFITS (below results so users see their search first) === */}
      <ListingBenefitsSection
        title="More Than a Directory Listing — Your AI-Powered Local Lead Engine"
        subtitle="When a business adds its website, LocalBest.ai instantly matches the listing to its brand — pulling in logos, colors, images, and content so it goes live polished and professional. Here's everything an active Best of Best Listing can include."
      />

      {/* CTA Section with Search & Filters */}
      {showFullDirectory && (
        <section className="py-16 bg-gradient-to-t from-[#7C3AED]/10 to-transparent">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Search & Filters Row */}
            <div className="glass-card p-6 rounded-2xl mb-8">
              <div className="flex flex-col lg:flex-row items-center gap-4">
                {/* Search Input */}
                <div className="relative flex-1 w-full">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search businesses..."
                    className="pl-11 pr-12 py-3 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] text-base"
                    data-testid="cta-search-input"
                  />
                  {/* Voice Search Button */}
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    <VoiceSearchButton 
                      onResult={(text) => setSearchQuery(text)} 
                      size="small"
                    />
                  </div>
                </div>
                
                {/* State Dropdown */}
                <div className="w-full lg:w-48">
                  <select
                    value={stateFilter}
                    onChange={(e) => setStateFilter(e.target.value)}
                    className="w-full bg-[#0F172A] border border-white/10 rounded-lg px-3 py-3 text-white text-sm focus:outline-none focus:border-[#00E5FF] [&>option]:bg-[#0F172A] [&>option]:text-white"
                    data-testid="cta-state-filter"
                  >
                    <option value="">All States</option>
                    {US_STATES.map((state) => (
                      <option key={state.code} value={state.code}>{state.name}</option>
                    ))}
                  </select>
                </div>
                
                {/* City Input */}
                <div className="w-full lg:w-48">
                  <Input
                    value={cityFilter}
                    onChange={(e) => setCityFilter(e.target.value)}
                    placeholder="Enter city..."
                    className="py-3 bg-white/5 border-white/10 text-white placeholder:text-[#64748B] text-sm"
                    data-testid="cta-city-filter"
                  />
                </div>
                
                {/* List Your Business Button */}
                <Link to="/submit" className="w-full lg:w-auto">
                  <Button className="btn-primary w-full lg:w-auto whitespace-nowrap">
                    Create My Free Listing <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
              
              {/* Active Filters */}
              {(searchQuery || cityFilter || stateFilter) && (
                <div className="flex items-center gap-2 mt-4 pt-4 border-t border-white/10">
                  <span className="text-[#94A3B8] text-sm">Active filters:</span>
                  {searchQuery && (
                    <span className="px-2 py-1 rounded-full bg-[#7C3AED]/20 text-[#7C3AED] text-xs flex items-center gap-1">
                      "{searchQuery}" <button onClick={() => setSearchQuery("")}><X className="w-3 h-3" /></button>
                    </span>
                  )}
                  {stateFilter && (
                    <span className="px-2 py-1 rounded-full bg-[#00E5FF]/20 text-[#00E5FF] text-xs flex items-center gap-1">
                      {US_STATES.find(s => s.code === stateFilter)?.name} <button onClick={() => setStateFilter("")}><X className="w-3 h-3" /></button>
                    </span>
                  )}
                  {cityFilter && (
                    <span className="px-2 py-1 rounded-full bg-[#10B981]/20 text-[#10B981] text-xs flex items-center gap-1">
                      {cityFilter} <button onClick={() => setCityFilter("")}><X className="w-3 h-3" /></button>
                    </span>
                  )}
                  <button onClick={clearFilters} className="text-[#94A3B8] text-xs hover:text-white ml-2">Clear all</button>
                </div>
              )}
            </div>
            
            {/* CTA Text */}
            <div className="text-center">
              <h2 className="text-2xl md:text-3xl font-bold font-['Outfit'] text-white mb-4">
                Own a Business? List It Free!
              </h2>
              <p className="text-[#94A3B8] mb-2">
                Includes AI Business Hero + Dashboard Access
              </p>
              <p className="text-[#64748B] text-sm mb-8">
                Join thousands of businesses. Get found by customers and grow.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/submit">
                  <Button className="btn-primary">
                    Create My Free Listing <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      <BackgroundVideoStrip src={BG_VIDEOS.directory} testid="directory-bg-video" />
    </div>
  );
}
